<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'added']);

const { t } = useI18n();
const store = useStore();

const form = reactive({
  email: '',
  name: '',
  role: 'agent',
});

const rules = {
  email: { required, email },
  name: { required },
  role: { required },
};

const v$ = useVuelidate(rules, form);

const isSubmitting = ref(false);
const errorMessage = ref('');
const result = ref(null); // { user, generated_password }

const onboardingMessage = computed(() => {
  if (!result.value?.generated_password) return '';
  return t('SUPER_ADMIN.ADD_USER.ONBOARDING_MESSAGE', {
    name: result.value.user.name,
    email: result.value.user.email,
    password: result.value.generated_password,
  });
});

const submit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    result.value = await store.dispatch('superAdminAccounts/addAccountUser', {
      accountId: props.accountId,
      email: form.email,
      name: form.name,
      role: form.role,
    });
    emit('added');
  } catch (error) {
    errorMessage.value = error?.message || t('SUPER_ADMIN.ADD_USER.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const copyMessage = async () => {
  await copyTextToClipboard(onboardingMessage.value);
  useAlert(t('SUPER_ADMIN.ADD_USER.MESSAGE_COPIED'));
};
</script>

<template>
  <div class="p-6">
    <template v-if="!result">
      <h3 class="text-lg font-medium text-n-slate-12 mb-4">
        {{ t('SUPER_ADMIN.ADD_USER.TITLE') }}
      </h3>

      <form class="grid gap-4" @submit.prevent="submit">
        <WithLabel :label="t('SUPER_ADMIN.ADD_USER.EMAIL.LABEL')">
          <NextInput
            v-model="form.email"
            type="email"
            class="w-full"
            :placeholder="t('SUPER_ADMIN.ADD_USER.EMAIL.PLACEHOLDER')"
          />
        </WithLabel>

        <WithLabel :label="t('SUPER_ADMIN.ADD_USER.NAME.LABEL')">
          <NextInput
            v-model="form.name"
            type="text"
            class="w-full"
            :placeholder="t('SUPER_ADMIN.ADD_USER.NAME.PLACEHOLDER')"
          />
        </WithLabel>

        <WithLabel :label="t('SUPER_ADMIN.ADD_USER.ROLE.LABEL')">
          <select v-model="form.role" class="w-full">
            <option value="agent">
              {{ t('SUPER_ADMIN.ADD_USER.ROLE.AGENT') }}
            </option>
            <option value="administrator">
              {{ t('SUPER_ADMIN.ADD_USER.ROLE.ADMINISTRATOR') }}
            </option>
          </select>
        </WithLabel>

        <p v-if="errorMessage" class="text-n-ruby-11 text-sm">
          {{ errorMessage }}
        </p>

        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            type="button"
            :label="t('SUPER_ADMIN.CANCEL')"
            @click="emit('close')"
          />
          <Button
            type="submit"
            :is-loading="isSubmitting"
            :label="t('SUPER_ADMIN.ADD_USER.SUBMIT')"
          />
        </div>
      </form>
    </template>

    <template v-else>
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        {{ t('SUPER_ADMIN.ADD_USER.SUCCESS_TITLE') }}
      </h3>

      <p
        v-if="!result.generated_password"
        class="text-body-main text-n-slate-11 mb-4"
      >
        {{ t('SUPER_ADMIN.ADD_USER.EXISTING_USER_LINKED') }}
      </p>

      <template v-else>
        <div
          class="bg-n-slate-2 rounded-lg p-4 mb-4 whitespace-pre-wrap text-body-main text-n-slate-12"
        >
          {{ onboardingMessage }}
        </div>
        <div class="flex justify-end gap-2 mb-2">
          <Button
            faded
            slate
            :label="t('SUPER_ADMIN.ADD_USER.COPY_MESSAGE')"
            @click="copyMessage"
          />
        </div>
      </template>

      <div class="flex justify-end">
        <Button
          :label="t('SUPER_ADMIN.ADD_USER.DONE')"
          @click="emit('close')"
        />
      </div>
    </template>
  </div>
</template>
